<template>
    <div class="report-card-page">
        <div class="report-card-header">
            <b-button size="sm" variant="outline-secondary" @click="goBack">
                <i class="fas fa-arrow-left"></i>
                К списку
            </b-button>
            <div class="report-card-header__title">
                <h5 v-if="report">Отчёт №{{ report.shift_id }}</h5>
                <h5 v-else>Отчёт</h5>
                <router-link
                    v-if="objectCardLink"
                    :to="objectCardLink"
                    class="report-object-link"
                >
                    {{ report.object.name }}
                </router-link>
                <span v-else-if="report && report.object.name">{{ report.object.name }}</span>
            </div>
            <div v-if="report" class="report-card-header__actions">
                <ReportActionsMenu
                    :report="report"
                    button-label="Действия"
                    :show-photo-actions="false"
                    @upload-photos="openPhotoUpload"
                    @resend-photos="resendPhotos"
                    @resend-report="resendTelegramReport"
                    @delete-request="openDeleteRequest"
                    @copy-link="copyReportLink"
                />
            </div>
        </div>

        <div v-if="loading" class="report-card-loading">
            <b-spinner />
            <span>Загрузка отчёта...</span>
        </div>

        <div v-else-if="report" class="report-card-content">
            <b-alert v-if="snapshotIncomplete" show variant="warning" class="mb-0">
                <strong>Исторический снимок этого старого отчёта неполный.</strong>
                Фактически поданные объёмы сохранены, но часть справочных названий или норм не была
                зафиксирована в момент подачи и восстановлена из доступных связей.
            </b-alert>
            <section v-if="isAnnulled" class="report-annulled-warning" role="alert">
                <div class="report-annulled-warning__title">
                    <i class="fas fa-ban"></i>
                    АННУЛИРОВАН — НЕ УЧИТЫВАТЬ
                </div>
                <div class="report-annulled-warning__details">
                    <div>
                        <small>Причина аннулирования</small>
                        <strong>{{ annulment.reason || 'Не указана' }}</strong>
                    </div>
                    <div>
                        <small>Заявку на аннулирование создал</small>
                        <strong>{{ annulmentRequestAuthorName }}</strong>
                        <router-link v-if="annulment.unified_request_id" :to="{name: 'request-card', params: {id: annulment.unified_request_id}}">Открыть заявку</router-link>
                    </div>
                    <div>
                        <small>Аннулирован</small>
                        <strong>{{ formatDate(annulment.annulled_at) }}</strong>
                    </div>
                </div>
            </section>

            <section class="report-panel report-summary">
                <div class="report-summary-main">
                    <div>
                        <small>Смена</small>
                        <strong>{{ formatDate(report.date_time_begin) }}</strong>
                        <strong class="report-shift-end">до {{ formatDate(report.date_time_end) }}</strong>
                        <span v-if="report.date_time_send" class="report-submitted-text">
                            подан {{ formatDate(report.date_time_send) }}{{ submissionDelayText ? ` (${submissionDelayText})` : '' }}
                        </span>
                    </div>
                    <div>
                        <small>Автор</small>
                        <strong>
                            <TelegramUserLink :user="report.author" fallback="-" name-order="last-first" />
                        </strong>
                        <a v-if="report.author.phone" :href="phoneHref(report.author.phone)">
                            {{ formatPhone(report.author.phone) }}
                        </a>
                    </div>
                    <div>
                        <small>Команда объекта</small>
                        <span class="report-team-line report-team-line--lead">
                            куратор
                            <TelegramUserLink :user="objectCurator" fallback="Куратор не указан" name-order="last-first" />
                        </span>
                        <span v-if="objectMainStaff && objectMainStaff.user_id" class="report-team-line">
                            прораб <TelegramUserLink :user="objectMainStaff" fallback="-" name-order="last-first" />
                        </span>
                        <div v-if="objectHasAccessUsers.length" class="report-team-access">
                            <small>Имеющие доступ</small>
                            <div class="report-team-access__chips">
                                <span
                                    v-for="user in objectHasAccessUsers"
                                    :key="user.user_id"
                                    class="report-team-access__chip"
                                >
                                    <TelegramUserLink :user="user" fallback="-" name-order="last-first" />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <small>Рабочие</small>
                        <strong>{{ report.quantity_of_workers || 0 }}</strong>
                        <span>обед {{ report.pause_time || 0 }} мин.</span>
                    </div>
                </div>
                <ReportBadges :report="report" show-empty />
            </section>

            <section v-if="hasExplanations" class="report-panel report-explanations">
                <div v-if="report.reason_for_delay" class="report-explanation report-explanation--late">
                    <strong><i class="fas fa-clock"></i> Причина просрочки</strong>
                    <blockquote class="user-quote report-explanation-quote" style="white-space: pre-wrap">{{ report.reason_for_delay }}</blockquote>
                        <footer>
                            <span>- заверяет</span>
                            <TelegramUserLink :user="report.author" fallback="автор без Telegram" name-order="last-first" />
                        </footer>
                </div>
                <div v-if="report.empty_photo_comment" class="report-explanation report-explanation--photo">
                    <strong><i class="fas fa-camera"></i> Причина неподачи фото</strong>
                    <blockquote class="user-quote report-explanation-quote" style="white-space: pre-wrap">{{ report.empty_photo_comment }}</blockquote>
                        <footer>
                            <span>- заверяет</span>
                            <TelegramUserLink :user="report.author" fallback="автор без Telegram" name-order="last-first" />
                        </footer>
                </div>
            </section>

            <section class="report-panel">
                <div class="report-section-title">
                    <button
                        type="button"
                        class="report-section-toggle"
                        v-b-toggle.report-media-collapse
                    >
                        <i :class="mediaVisible ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                        <span>Фото и медиа</span>
                    </button>
                    <div class="report-section-meta report-media-summary">
                        <span>{{ media.length }} файлов</span>
                        <span
                            v-for="item in mediaDeliveryBadges"
                            :key="item.key"
                            class="report-media-summary__badge"
                            :class="`report-media-summary__badge--${item.variant}`"
                        >
                            {{ item.text }}
                        </span>
                    </div>
                    <div v-if="canManageReportPhotos" class="report-media-action-buttons">
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            @click="openPhotoUpload(report)"
                        >
                            <i class="fas fa-cloud-upload-alt"></i>
                            Догрузить фото
                        </b-button>
                        <b-button
                            v-if="canResendReportPhotos"
                            size="sm"
                            variant="outline-warning"
                            @click="resendPhotos(report)"
                        >
                            <i class="fas fa-paper-plane"></i>
                            Отправить неотправленные
                        </b-button>
                    </div>
                </div>
                <b-collapse id="report-media-collapse" v-model="mediaVisible">
                    <ReportMediaGrid
                        :files="media"
                        :report-id="report.shift_id"
                        :annulled="isAnnulled"
                        :annulment-reason="annulment.reason || ''"
                    />
                </b-collapse>
            </section>

            <section class="report-panel">
                <div class="report-section-title">
                    <div>
                        <h6>Основные работы</h6>
                        <div class="report-section-note" role="note">
                            <i class="fas fa-info-circle" aria-hidden="true"></i>
                            <span>
                                <strong>Данные ниже зафиксированы на момент подачи отчёта и не обновляются.</strong>
                                Текущее состояние объекта смотрите по кнопке «Текущий прогресс объекта».
                            </span>
                        </div>
                    </div>
                    <div class="report-section-actions">
                        <div class="report-section-meta">{{ works.length }} поз.</div>
                        <b-button
                            v-if="objectCardLink"
                            :to="objectCardLink"
                            size="sm"
                            variant="primary"
                            class="report-current-progress-link"
                        >
                            <i class="fas fa-chart-line"></i>
                            Текущий прогресс объекта
                        </b-button>
                    </div>
                </div>
                <div v-if="works.length" class="table-responsive">
                    <table class="table table-sm report-table report-works-table">
                        <thead>
                        <tr>
                            <th>Работа</th>
                            <th class="text-right">Смена</th>
                            <th class="text-center report-workers-col">Рабочие</th>
                            <th>Материалы</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="work in works" :key="work.work_id">
                            <td data-label="Работа">
                                <strong>{{ work.project_name || work.name }}</strong>
                                <small v-if="work.project_name && work.name" class="d-block text-muted">{{ work.name }}</small>
                            </td>
                            <td class="text-right" data-label="Смена">
                                <div class="report-work-value">
                                    <strong>{{ formatNumber(work.value) }} {{ work.unit }}</strong>
                                    <small v-if="work.progress" class="text-muted">
                                        накоплено {{ formatAmount(work.progress.completed_value, work.unit) }}
                                    </small>
                                </div>
                                <div v-if="work.progress" class="report-work-progress">
                                    <div class="report-progress-context">на момент этого отчёта</div>
                                    <div class="report-progress-bar report-progress-bar--sm">
                                        <span
                                            :class="progressBarClass(work.progress.progress_percent)"
                                            :style="{ width: `${work.progress.progress_percent_capped}%` }"
                                        ></span>
                                    </div>
                                    <div class="report-work-progress__meta">
                                        <span>{{ work.progress.progress_percent }}%</span>
                                        <span>{{ formatAmount(work.progress.completed_value, work.unit) }} из {{ formatAmount(work.progress.plan_value, work.unit) }}</span>
                                    </div>
                                </div>
                            </td>
                            <td class="text-center report-workers-col" data-label="Рабочие">{{ work.person_counts || 0 }}</td>
                            <td data-label="Материалы">
                                <div v-if="work.materials && work.materials.length" class="report-material-list">
                                    <div
                                        v-for="material in work.materials"
                                        :key="material.id || material.material_id"
                                        class="report-material-item"
                                        :class="material.progress ? `report-material-item--${material.progress.status}` : ''"
                                    >
                                        <div class="report-material-item__header">
                                            <span>
                                                {{ material.name }}:
                                                <strong>{{ formatNumber(material.value) }}</strong> {{ material.unit }}
                                            </span>
                                            <b-badge v-if="material.progress" :variant="materialStatusVariant(material.progress)">
                                                {{ material.progress.status_label }}
                                            </b-badge>
                                        </div>
                                        <div v-if="material.progress" class="report-material-scale" :title="materialScaleTitle(material.progress)">
                                            <span class="report-material-scale__context">на момент этого отчёта</span>
                                            <span class="report-material-scale__label report-material-scale__label--left">Экономия</span>
                                            <span class="report-material-scale__label report-material-scale__label--norm">По норме</span>
                                            <span class="report-material-scale__label report-material-scale__label--right">Перерасход</span>
                                            <span class="report-material-scale__norm" :style="materialNormBandStyle(material.progress)"></span>
                                            <span
                                                class="report-material-scale__tick"
                                                :style="{ left: `${materialNormLeftPosition(material.progress)}%` }"
                                            ></span>
                                            <span
                                                class="report-material-scale__tick"
                                                :style="{ left: `${materialNormRightPosition(material.progress)}%` }"
                                            ></span>
                                            <span
                                                :class="materialScaleFillClass(material.progress)"
                                                :style="{ width: `${materialScalePosition(material.progress)}%` }"
                                            ></span>
                                            <span
                                                :class="materialScaleMarkerClass(material.progress)"
                                                :style="{ left: `${materialScalePosition(material.progress)}%` }"
                                            ></span>
                                        </div>
                                        <div v-if="material.progress" class="report-material-item__meta">
                                            <span>По факту {{ formatAmount(material.progress.fact_value, material.unit) }}</span>
                                            <span>По норме {{ formatAmount(material.progress.norm_value, material.unit) }}</span>
                                            <span>{{ materialRatioText(material.progress) }}</span>
                                            <span :class="{ 'report-material-item__meta-danger': material.progress.limit_error }">
                                                {{ materialDeviationText(material.progress) }}
                                            </span>
                                            <span>{{ materialLimitText(material.progress) }}</span>
                                        </div>
                                    </div>
                                </div>
                                <span v-else class="text-muted">-</span>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="text-muted py-2">Основные работы не указаны</div>
            </section>

            <section class="report-panel">
                <div class="report-section-title">
                    <h6>Топливо</h6>
                    <div class="report-section-meta">{{ fuel.length }} поз.</div>
                </div>
                <div v-if="fuel.length" class="table-responsive">
                    <table class="table table-sm report-table">
                        <thead>
                        <tr>
                            <th>Топливо</th>
                            <th class="text-right">Количество</th>
                            <th>Техника / примечание</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="item in fuel" :key="item.fuel_id">
                            <td data-label="Топливо">{{ item.name }}</td>
                            <td class="text-right" data-label="Количество">{{ formatNumber(item.value) }} {{ item.unit }}</td>
                            <td data-label="Техника / примечание">
                                <span v-if="item.notes && item.notes.length">{{ item.notes.join(', ') }}</span>
                                <span v-else class="text-muted">-</span>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="report-empty-note">
                    <i class="fas fa-gas-pump"></i>
                    Без топлива
                </div>
            </section>

            <section
                v-if="secondWorks.length || notes.length"
                class="report-panel report-notes-grid"
                :class="{'report-notes-grid--single': !secondWorks.length || !notes.length}"
            >
                <div v-if="secondWorks.length" class="report-text-section">
                    <div class="report-section-title">
                        <h6>Вторичные работы</h6>
                    </div>
                    <div class="report-text-blocks">
                        <p v-for="(item, index) in secondWorks" :key="`sw_${index}`" class="report-text-block">{{ item }}</p>
                    </div>
                </div>
                <div v-if="notes.length" class="report-text-section">
                    <div class="report-section-title">
                        <h6>Рекомендации</h6>
                    </div>
                    <div class="report-text-blocks">
                        <p v-for="(item, index) in notes" :key="`note_${index}`" class="report-text-block">{{ item }}</p>
                    </div>
                </div>
            </section>

            <section v-if="telegramText" class="report-panel">
                <div class="report-telegram-actions">
                    <b-button v-b-toggle.telegram-text size="sm" variant="outline-secondary">
                        <i class="fab fa-telegram-plane"></i>
                        Текст Telegram
                    </b-button>
                    <b-button
                        v-if="telegramUrl"
                        :href="telegramUrl"
                        target="_blank"
                        rel="noopener"
                        size="sm"
                        variant="outline-info"
                    >
                        <i class="fab fa-telegram-plane"></i>
                        Открыть в Telegram
                    </b-button>
                </div>
                <b-collapse id="telegram-text" class="mt-3">
                    <div class="report-telegram-text" v-html="telegramText"></div>
                </b-collapse>
            </section>

            <ReportActivity
                :report-id="report.shift_id"
                :comments="comments"
                :history="history"
                @changed="reloadActivity"
            />
        </div>

        <div v-else class="text-muted text-center py-5">
            Отчёт не найден
        </div>

        <ReportPhotoUploadModal
            v-model="photoUploadModalVisible"
            :report="photoUploadReport"
            @uploaded="onPhotosUploaded"
        />

        <ReportDeleteRequestModal
            v-model="deleteRequestModalVisible"
            :reports="deleteRequestReports"
            @created="onDeleteRequestCreated"
        />
    </div>
</template>

<script>
import {formatPhone, phoneHref} from '@/utils/phone';
import ReportBadges from './ReportBadges.vue';
import ReportActionsMenu from './ReportActionsMenu.vue';
import ReportMediaGrid from './ReportMediaGrid.vue';
import ReportActivity from './ReportActivity.vue';
import ReportDeleteRequestModal from './ReportDeleteRequestModal.vue';
import ReportPhotoUploadModal from './ReportPhotoUploadModal.vue';
import {buildMediaDeliveryBadges, hasUnsentPhotosToResend} from './reportMediaStatus';
import {canRequestReportAnnulment, isReportAnnulled} from './reportAnnulmentAccess';
import TelegramUserLink from '@/views/Requests/RequestCard/components/TelegramUserLink.vue';
import {updateRecentWorkItem} from '@/utils/recentWork';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

export default {
    name: 'ReportCard',
    components: {
        ReportBadges,
        ReportActionsMenu,
        ReportMediaGrid,
        ReportActivity,
        ReportDeleteRequestModal,
        ReportPhotoUploadModal,
        TelegramUserLink
    },
    data() {
        return {
            loading: false,
            payload: null,
            mediaVisible: true,
            photoUploadModalVisible: false,
            photoUploadReport: null,
            deleteRequestModalVisible: false
        };
    },
    computed: {
        report() {
            return this.payload && this.payload.report ? this.payload.report : null;
        },
        snapshot() {
            return this.payload && this.payload.snapshot ? this.payload.snapshot : null;
        },
        snapshotIncomplete() {
            return Boolean(this.snapshot && this.snapshot.complete === false);
        },
        annulment() {
            return this.report && this.report.annulment ? this.report.annulment : {};
        },
        isAnnulled() {
            return isReportAnnulled(this.report);
        },
        canRequestAnnulment() {
            return canRequestReportAnnulment(
                this.report,
                this.currentUser,
                this.$store.getters.isAdmin
            );
        },
        annulmentRequestAuthorName() {
            const user = this.annulment.request_author || {};
            return user.full_name ||
                [user.last_name, user.first_name].filter(Boolean).join(' ') ||
                'Автор заявки не найден';
        },
        sections() {
            return this.payload && this.payload.sections ? this.payload.sections : {};
        },
        works() {
            return this.sections.works || [];
        },
        fuel() {
            return this.sections.fuel || [];
        },
        secondWorks() {
            return this.sections.second_works || [];
        },
        notes() {
            return this.sections.notes || [];
        },
        media() {
            return this.payload && Array.isArray(this.payload.media) ? this.payload.media : [];
        },
        comments() {
            return this.payload && Array.isArray(this.payload.comments) ? this.payload.comments : [];
        },
        history() {
            return this.payload && Array.isArray(this.payload.history) ? this.payload.history : [];
        },
        currentUser() {
            return this.$store.getters.currentUser || {};
        },
        currentUserId() {
            return Number(this.currentUser.userId || this.currentUser.user_id || this.currentUser.id);
        },
        isAdminLikeUser() {
            const roles = Array.isArray(this.currentUser.roles) ? this.currentUser.roles : [];
            return this.$store.getters.isAdmin || roles.indexOf('Top') !== -1;
        },
        telegramText() {
            return this.payload ? this.payload.telegram_text : '';
        },
        telegramUrl() {
            return this.report && this.report.telegram_url ? this.report.telegram_url : '';
        },
        hasExplanations() {
            return Boolean(this.report && (this.report.reason_for_delay || this.report.empty_photo_comment));
        },
        submissionDelayText() {
            return this.report &&
                this.report.submission &&
                this.report.submission.delay &&
                this.report.submission.delay.text
                ? this.report.submission.delay.text
                : '';
        },
        objectCardLink() {
            return this.report && this.report.object && this.report.object.id
                ? `/objects/${this.report.object.id}`
                : '';
        },
        objectCurator() {
            return this.report && this.report.object ? this.report.object.curator : null;
        },
        objectMainStaff() {
            return this.report && this.report.object ? this.report.object.main_staff : null;
        },
        objectHasAccessUsers() {
            return this.report &&
                this.report.object &&
                Array.isArray(this.report.object.has_access_users)
                ? this.report.object.has_access_users
                : [];
        },
        mediaStats() {
            return this.report && this.report.stats ? this.report.stats : {};
        },
        mediaDeliveryBadges() {
            return buildMediaDeliveryBadges(this.report, this.media.length);
        },
        canManageReportPhotos() {
            if (this.isAnnulled || !this.report || !this.report.object) {
                return false;
            }
            if (this.isAdminLikeUser) {
                return true;
            }
            if (!this.currentUserId) {
                return false;
            }
            const object = this.report.object;
            const allowedIds = [
                object.curator_id,
                object.main_staff_id
            ].concat(this.objectHasAccessUsers.map(user => user.user_id));

            return allowedIds.some(id => Number(id) === this.currentUserId);
        },
        canResendReportPhotos() {
            return this.canManageReportPhotos && hasUnsentPhotosToResend(this.report);
        },
        deleteRequestReports() {
            return this.report ? [this.report] : [];
        }
    },
    methods: {
        formatPhone,
        phoneHref,
        async loadReport() {
            this.loading = true;
            try {
                this.payload = await this.$store.dispatch('reports/fetchReport', this.$route.params.id);
                if (this.report) {
                    updateRecentWorkItem(
                        this.currentUser,
                        'report',
                        this.$route.params.id,
                        {
                            label: this.report.object && this.report.object.name
                                ? this.report.object.name
                                : 'Сменный отчёт',
                            contextLabel: `Отчёт #${this.report.shift_id || this.$route.params.id}`
                        }
                    );
                }
                this.openPhotoUploadFromRouteIfNeeded();
            } finally {
                this.loading = false;
            }
        },
        async reloadActivity() {
            const data = await this.$store.dispatch('reports/fetchActivity', this.$route.params.id);
            this.payload = {
                ...this.payload,
                comments: data.comments || [],
                history: data.history || []
            };
        },
        goBack() {
            const id = this.report ? this.report.shift_id : this.$route.params.id;
            this.$router.push({path: '/reports', query: {highlight: id}});
        },
        openPhotoUpload(report) {
            if (!report || !report.shift_id || this.isAnnulled) return;
            this.photoUploadReport = report;
            this.photoUploadModalVisible = true;
        },
        openPhotoUploadFromRouteIfNeeded() {
            const query = this.$route.query || {};
            const shouldOpen =
                query.openPhotoUpload === 'true'
                || query.openPhotosModal === 'true';
            if (!shouldOpen || !this.report) return;

            this.openPhotoUpload(this.report);

            const nextQuery = {...query};
            delete nextQuery.openPhotoUpload;
            delete nextQuery.openPhotosModal;
            this.$router.replace({
                path: this.$route.path,
                query: nextQuery
            }).catch(() => {});
        },
        async onPhotosUploaded({uploadedCount}) {
            this.$bvToast.toast(`Принято файлов: ${uploadedCount || 0}. Отправка в Telegram запущена`, {
                title: 'Догрузка фото',
                variant: 'success',
                solid: true
            });
            await this.loadReport();
        },
        async resendPhotos(report) {
            if (!report || !report.shift_id || this.isAnnulled) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Отправить неотправленные фото отчёта №${report.shift_id} в Telegram?`,
                {
                    title: 'Отправка неотправленных фото',
                    okTitle: 'Отправить',
                    cancelTitle: 'Отмена',
                    okVariant: 'primary'
                }
            );
            if (!confirmed) return;

            try {
                const result = await this.$store.dispatch('reports/resendPhotos', report.shift_id);
                notifyInstallPromotionSuccess({source: 'report-photos-resent'});
                this.$bvToast.toast(
                    `Запущена отправка ${result && result.mediaCount ? result.mediaCount : ''} фото`,
                    {
                        title: 'Отправка неотправленных фото',
                        variant: 'info',
                        solid: true
                    }
                );
            } catch (err) {
                this.$bvToast.toast(
                    err && err.response && err.response.data && err.response.data.msg
                        ? err.response.data.msg
                        : 'Не удалось запустить отправку неотправленных фото',
                    {
                        title: 'Отправка неотправленных фото',
                        variant: 'danger',
                        solid: true
                    }
                );
            }
        },
        async resendTelegramReport(report) {
            if (!report || !report.shift_id || this.isAnnulled) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Отправить отчёт №${report.shift_id} новым сообщением в Telegram?`,
                {
                    title: 'Повторная отправка отчёта',
                    okTitle: 'Отправить',
                    cancelTitle: 'Отмена',
                    okVariant: 'primary'
                }
            );
            if (!confirmed) return;

            try {
                await this.$store.dispatch('reports/resendTelegramReport', report.shift_id);
                notifyInstallPromotionSuccess({source: 'report-resent'});
                this.$bvToast.toast('Повторная отправка отчёта запущена', {
                    title: 'Telegram',
                    variant: 'info',
                    solid: true
                });
                await this.reloadActivity().catch(() => {});
            } catch (err) {
                this.$bvToast.toast(
                    err && err.response && err.response.data && err.response.data.msg
                        ? err.response.data.msg
                        : 'Не удалось запустить повторную отправку отчёта',
                    {
                        title: 'Telegram',
                        variant: 'danger',
                        solid: true
                    }
                );
            }
        },
        openDeleteRequest() {
            if (!this.canRequestAnnulment) return;
            this.deleteRequestModalVisible = true;
        },
        async copyReportLink(report) {
            if (!report || !report.shift_id) return;
            const url = `${window.location.origin}/reports/${report.shift_id}`;
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(url);
                } else {
                    const input = document.createElement('textarea');
                    input.value = url;
                    input.setAttribute('readonly', 'readonly');
                    input.style.position = 'fixed';
                    input.style.opacity = '0';
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                }
                this.$bvToast.toast('Ссылка на отчёт скопирована', {
                    title: 'Отчёты',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                this.$bvToast.toast(url, {
                    title: 'Ссылка на отчёт',
                    variant: 'warning',
                    solid: true
                });
            }
        },
        async onDeleteRequestCreated({request, approved, sent}) {
            const requestId = request && request.id ? request.id : null;
            const message = approved
                ? `Заявка №${requestId} согласована, отчёт аннулирован`
                : sent
                    ? `Заявка №${requestId} отправлена куратору`
                    : `Заявка №${requestId} сохранена как черновик`;
            this.$bvToast.toast(message, {
                title: 'Аннулирование отчёта',
                variant: approved ? 'success' : 'info',
                solid: true
            });
            if (approved) {
                await this.loadReport();
            }
        },
        formatDate(value) {
            return value ? this.$moment(value).format('DD.MM.YYYY dd HH:mm') : '-';
        },
        formatNumber(value) {
            const number = Number(value);
            if (!Number.isFinite(number)) return '0';
            return number.toLocaleString('ru-RU', {maximumFractionDigits: 3});
        },
        numberText(value, maximumFractionDigits = 2) {
            const number = Number(value);
            if (!Number.isFinite(number)) return '0';
            return number.toLocaleString('ru-RU', {
                minimumFractionDigits: 0,
                maximumFractionDigits
            });
        },
        formatAmount(value, unit) {
            return `${this.numberText(value, 3)}${unit ? ` ${unit}` : ''}`;
        },
        progressBarClass(percent) {
            const value = Number(percent) || 0;
            if (value >= 100) {
                return 'report-progress-bar__fill report-progress-bar__fill--success';
            }
            if (value > 0) {
                return 'report-progress-bar__fill report-progress-bar__fill--primary';
            }
            return 'report-progress-bar__fill report-progress-bar__fill--muted';
        },
        materialStatusVariant(material) {
            const status = material && material.status;
            if (status === 'over_limit') {
                return 'danger';
            }
            if (status === 'under_limit') {
                return 'info';
            }
            return 'success';
        },
        materialScaleLimit(material) {
            const limit = Number(material && material.limit_percent);
            if (!Number.isFinite(limit) || limit < 0) {
                return 0;
            }
            return Math.min(limit, 100);
        },
        materialScalePosition(material) {
            const ratio = Number(material && material.ratio_percent);
            if (!Number.isFinite(ratio)) {
                return 50;
            }
            return Math.max(0, Math.min(ratio, 200)) / 2;
        },
        materialNormLeftPosition(material) {
            return Math.max(0, (100 - this.materialScaleLimit(material)) / 2);
        },
        materialNormRightPosition(material) {
            return Math.min(100, (100 + this.materialScaleLimit(material)) / 2);
        },
        materialNormBandStyle(material) {
            const left = this.materialNormLeftPosition(material);
            const right = this.materialNormRightPosition(material);
            return {
                left: `${left}%`,
                width: `${Math.max(1, right - left)}%`
            };
        },
        materialScaleStatus(material) {
            const ratio = Number(material && material.ratio_percent);
            if (!Number.isFinite(ratio)) {
                return 'normal';
            }
            const limit = this.materialScaleLimit(material);
            if (ratio > 100 + limit) {
                return 'over_limit';
            }
            if (ratio < 100 - limit) {
                return 'under_limit';
            }
            return 'normal';
        },
        materialScaleFillClass(material) {
            const status = this.materialScaleStatus(material);
            return [
                'report-material-scale__fill',
                `report-material-scale__fill--${status}`
            ];
        },
        materialScaleMarkerClass(material) {
            const status = this.materialScaleStatus(material);
            return [
                'report-material-scale__marker',
                `report-material-scale__marker--${status}`
            ];
        },
        materialScaleTitle(material) {
            const ratio = Number.isFinite(Number(material && material.ratio_percent))
                ? `${this.numberText(material.ratio_percent, 1)}% от нормы`
                : 'По факту относительно нормы не рассчитано';
            const limit = `допуск +/-${this.numberText(this.materialScaleLimit(material), 2)}%`;
            return `${ratio}, ${limit}`;
        },
        materialRatioText(material) {
            if (!Number.isFinite(Number(material && material.ratio_percent))) {
                return 'доля не рассчитана';
            }
            return `${this.numberText(material.ratio_percent, 1)}% от нормы`;
        },
        materialDeviationText(material) {
            if (!Number.isFinite(Number(material && material.deviation_percent))) {
                return 'отклонение не рассчитано';
            }
            const value = Number(material.deviation_percent);
            return `${value > 0 ? '+' : ''}${this.numberText(value, 2)}%`;
        },
        materialLimitText(material) {
            return `допуск +/-${this.numberText(this.materialScaleLimit(material), 2)}%`;
        }
    },
    mounted() {
        this.loadReport();
    }
}
</script>

<style scoped>
.report-card-page {
    background: #f4f6f8;
    min-height: calc(100vh - 48px);
    padding: 10px 12px 18px;
}

.report-card-header {
    align-items: center;
    display: flex;
    gap: 12px;
    margin-bottom: 10px;
}

.report-card-header__title {
    min-width: 0;
}

.report-card-header__title h5 {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
}

.report-card-header__title span,
.report-object-link {
    color: #6c757d;
    display: block;
    font-size: 13px;
}

.report-card-header__actions {
    display: flex;
    flex: 0 0 auto;
    margin-left: auto;
}

.report-card-header__actions >>> .report-actions-menu,
.report-card-header__actions >>> .dropdown {
    width: 100%;
}

.report-card-header__actions >>> .dropdown-toggle {
    width: 100%;
}

.report-card-header__actions .btn i {
    margin-right: 4px;
}

.report-object-link:hover {
    color: #0056b3;
}

.report-card-loading {
    align-items: center;
    color: #6c757d;
    display: flex;
    gap: 10px;
    justify-content: center;
    min-height: 240px;
}

.report-card-content {
    display: grid;
    gap: 10px;
}

.report-annulled-warning {
    background: #7f101b;
    border: 3px solid #dc3545;
    border-radius: 7px;
    box-shadow: 0 3px 10px rgba(114, 28, 36, .24);
    color: #fff;
    padding: 13px 15px;
}

.report-annulled-warning__title {
    align-items: center;
    display: flex;
    font-size: 20px;
    font-weight: 900;
    gap: 9px;
    letter-spacing: .02em;
    text-transform: uppercase;
}

.report-annulled-warning__details {
    display: grid;
    gap: 12px;
    grid-template-columns: minmax(0, 2fr) minmax(180px, 1fr) minmax(150px, .7fr);
    margin-top: 10px;
}

.report-annulled-warning__details small {
    color: #ffd8dc;
    display: block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.report-annulled-warning__details strong {
    display: block;
    overflow-wrap: anywhere;
}

.report-panel {
    background: #fff;
    border: 1px solid #dfe5eb;
    border-radius: 6px;
    padding: 12px;
}

.report-summary {
    display: grid;
    gap: 12px;
}

.report-summary-main {
    display: grid;
    gap: 10px;
    grid-template-columns: 2fr 1.2fr 1.35fr 1fr;
}

.report-summary-main div {
    min-width: 0;
}

.report-summary-main small,
.report-summary-main span {
    color: #6c757d;
    display: block;
}

.report-summary-main strong {
    display: block;
    overflow-wrap: anywhere;
}

.report-shift-end {
    margin-top: 2px;
}

.report-submitted-text {
    margin-top: 4px;
}

.report-team-line a {
    font-weight: 600;
}

.report-team-line--lead {
    color: #212529;
    font-weight: 700;
}

.report-team-access {
    margin-top: 6px;
}

.report-team-access__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 3px;
}

.report-team-access__chip {
    background: #eef4ff;
    border: 1px solid #bfd2ff;
    border-radius: 999px;
    color: #244985;
    display: inline-flex;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
    max-width: 100%;
    padding: 2px 7px;
}

.report-team-access__chip a {
    color: inherit;
}

.report-explanations {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.report-explanation {
    border-left: 4px solid #ffc107;
    padding-left: 10px;
}

.report-explanation--late {
    border-left-color: #dc3545;
}

.report-explanation-quote {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    color: #212529;
    margin: 7px 0 0;
    padding: 9px 10px;
    position: relative;
}

.report-explanation-quote::before {
    color: #adb5bd;
    content: '"';
    font-size: 28px;
    font-weight: 900;
    line-height: 1;
    position: absolute;
    right: 10px;
    top: 4px;
}

.report-explanation-quote p {
    font-style: italic;
    margin: 0;
    padding-right: 18px;
    white-space: pre-wrap;
}

.report-explanation-quote footer {
    align-items: center;
    color: #6c757d;
    display: flex;
    flex-wrap: wrap;
    font-size: 12px;
    font-weight: 700;
    gap: 5px;
    margin-top: 8px;
}

.report-explanation-quote footer a {
    font-weight: 800;
}

.report-section-title {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: space-between;
    margin-bottom: 10px;
}

.report-section-title h6 {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
}

.report-section-note {
    align-items: flex-start;
    background: #fff8e1;
    border: 1px solid #ecd486;
    border-radius: 6px;
    color: #5f4b00;
    display: flex;
    font-size: 12px;
    gap: 6px;
    line-height: 1.35;
    margin-top: 6px;
    max-width: 620px;
    padding: 6px 8px;
}

.report-section-note i {
    color: #b77900;
    margin-top: 2px;
}

.report-section-note strong {
    display: block;
}

.report-section-actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
}

.report-current-progress-link {
    font-weight: 700;
}

.report-current-progress-link i {
    margin-right: 4px;
}

.report-section-toggle {
    align-items: center;
    background: transparent;
    border: 0;
    color: #212529;
    display: inline-flex;
    font-size: 16px;
    font-weight: 700;
    gap: 8px;
    padding: 0;
}

.report-section-toggle:hover {
    color: #0056b3;
}

.report-section-meta {
    color: #6c757d;
    display: flex;
    font-size: 12px;
    gap: 10px;
}

.report-media-summary {
    align-items: center;
    flex-wrap: wrap;
}

.report-media-summary__badge {
    border: 1px solid #d7dde5;
    border-radius: 999px;
    color: #495057;
    font-weight: 700;
    line-height: 1.2;
    padding: 3px 7px;
}

.report-media-summary__badge--success {
    background: #e8f5ec;
    border-color: #b7dfc0;
    color: #1e7e34;
}

.report-media-summary__badge--warning {
    background: #fff7db;
    border-color: #f0d57a;
    color: #7a5600;
}

.report-media-summary__badge--info {
    background: #e4f6f9;
    border-color: #a8dde6;
    color: #0c5460;
}

.report-media-summary__badge--danger {
    background: #fde8eb;
    border-color: #f4b9c0;
    color: #b21f2d;
}

.report-media-summary__badge--muted {
    background: #f5f7fa;
    color: #6c757d;
}

.report-media-action-buttons {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.report-media-action-buttons .btn {
    font-weight: 700;
}

.report-media-action-buttons .btn i {
    margin-right: 4px;
}

.report-table {
    margin-bottom: 0;
}

.report-works-table {
    border-collapse: separate;
    border-spacing: 0 10px;
    margin-top: -10px;
}

.report-works-table thead th {
    border-bottom: 2px solid #cbd5e0;
}

.report-works-table tbody td {
    background: #f8fafc;
    border-bottom: 1px solid #d6dee8;
    border-top: 1px solid #d6dee8;
    padding: 12px 10px;
    vertical-align: top;
}

.report-works-table tbody tr:nth-child(even) td {
    background: #f1f5f9;
}

.report-works-table tbody td:first-child {
    border-left: 1px solid #d6dee8;
    border-radius: 6px 0 0 6px;
}

.report-works-table tbody td:last-child {
    border-radius: 0 6px 6px 0;
    border-right: 1px solid #d6dee8;
}

.report-work-value strong,
.report-work-value small {
    display: block;
}

.report-work-progress {
    margin-top: 5px;
    min-width: 150px;
}

.report-progress-context {
    color: #495057;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 2px;
    text-align: left;
}

.report-work-progress__meta {
    color: #6c757d;
    display: flex;
    font-size: 11px;
    gap: 8px;
    justify-content: space-between;
    line-height: 1.2;
    margin-top: 3px;
}

.report-progress-bar {
    background: #e9ecef;
    border-radius: 999px;
    height: 9px;
    overflow: hidden;
    position: relative;
    width: 100%;
}

.report-progress-bar--sm {
    height: 7px;
}

.report-progress-bar__fill {
    border-radius: inherit;
    display: block;
    height: 100%;
    min-width: 2px;
    transition: width .2s ease;
}

.report-progress-bar__fill--primary {
    background: #0d6efd;
}

.report-progress-bar__fill--success {
    background: #28a745;
}

.report-progress-bar__fill--muted {
    background: #adb5bd;
}

.report-material-list {
    display: grid;
    gap: 8px;
}

.report-material-item {
    border-left: 3px solid #d8dee6;
    padding-left: 8px;
}

.report-material-item--normal {
    border-left-color: #28a745;
}

.report-material-item--under_limit {
    border-left-color: #17a2b8;
}

.report-material-item--over_limit {
    border-left-color: #dc3545;
}

.report-material-item__header {
    align-items: flex-start;
    display: flex;
    gap: 8px;
    justify-content: space-between;
}

.report-material-item__header > span {
    min-width: 0;
    overflow-wrap: anywhere;
}

.report-material-item__meta {
    color: #6c757d;
    display: flex;
    flex-wrap: wrap;
    font-size: 11px;
    gap: 4px 10px;
    line-height: 1.2;
}

.report-material-item__meta-danger {
    color: #dc3545;
    font-weight: 700;
}

.report-material-scale {
    height: 43px;
    margin: 5px 0 6px;
    min-width: 210px;
    position: relative;
}

.report-material-scale::before {
    background: linear-gradient(90deg, #eef9fc 0%, #f8fafc 50%, #fff2f2 100%);
    border: 1px solid #dbe3ec;
    border-radius: 999px;
    bottom: 4px;
    content: "";
    height: 9px;
    left: 0;
    position: absolute;
    right: 0;
}

.report-material-scale__context {
    color: #495057;
    font-size: 10px;
    font-weight: 700;
    left: 0;
    line-height: 1;
    position: absolute;
    top: 0;
}

.report-material-scale__label {
    color: #7b8491;
    font-size: 9px;
    font-weight: 800;
    line-height: 1;
    pointer-events: none;
    position: absolute;
    top: 13px;
    z-index: 5;
}

.report-material-scale__label--left {
    color: #0c6374;
    left: 0;
}

.report-material-scale__label--norm {
    color: #0f5132;
    left: 50%;
    transform: translateX(-50%);
}

.report-material-scale__label--right {
    color: #842029;
    right: 0;
}

.report-material-scale__norm {
    background: rgba(40, 167, 69, .18);
    border-radius: 999px;
    bottom: 2px;
    height: 13px;
    position: absolute;
    z-index: 1;
}

.report-material-scale__tick {
    background: #343a40;
    border-radius: 2px;
    bottom: 0;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, .85);
    height: 18px;
    position: absolute;
    transform: translateX(-1px);
    width: 2px;
    z-index: 4;
}

.report-material-scale__fill {
    border-radius: 999px;
    bottom: 5px;
    height: 7px;
    left: 0;
    min-width: 2px;
    position: absolute;
    transition: width .2s ease;
    z-index: 2;
}

.report-material-scale__fill--normal {
    background: #28a745;
}

.report-material-scale__fill--under_limit {
    background: #17a2b8;
}

.report-material-scale__fill--over_limit {
    background: #dc3545;
}

.report-material-scale__marker {
    border: 2px solid #fff;
    border-radius: 999px;
    bottom: 2px;
    box-shadow: 0 1px 4px rgba(15, 23, 42, .25);
    height: 12px;
    position: absolute;
    transform: translateX(-6px);
    width: 12px;
    z-index: 5;
}

.report-material-scale__marker--normal {
    background: #28a745;
}

.report-material-scale__marker--under_limit {
    background: #17a2b8;
}

.report-material-scale__marker--over_limit {
    background: #dc3545;
}

.report-workers-col {
    min-width: 82px;
    width: 82px;
}

.report-empty-note {
    align-items: center;
    border: 1px dashed #cfd6df;
    border-radius: 6px;
    color: #6c757d;
    display: flex;
    gap: 8px;
    justify-content: center;
    min-height: 58px;
}

.report-notes-grid {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.report-notes-grid--single {
    grid-template-columns: 1fr;
}

.report-text-section {
    min-width: 0;
}

.report-text-blocks {
    display: grid;
    gap: 8px;
}

.report-text-block {
    line-height: 1.45;
    margin: 0;
    white-space: pre-wrap;
}

.report-text-block + .report-text-block {
    border-top: 1px solid #eef1f4;
    padding-top: 8px;
}

.report-telegram-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.report-telegram-actions .btn {
    align-items: center;
    display: inline-flex;
    font-weight: 700;
    gap: 6px;
    line-height: 1.2;
    min-height: 30px;
}

.report-telegram-actions .btn i {
    flex: 0 0 16px;
    line-height: 1;
    text-align: center;
}

.report-telegram-text {
    background: #f8f9fa;
    border: 1px solid #e6eaef;
    border-radius: 6px;
    line-height: 1.35;
    max-height: 420px;
    overflow: auto;
    padding: 10px;
    white-space: pre-wrap;
}

@media (max-width: 991.98px) {
    .report-summary-main {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 767.98px) {
    .report-annulled-warning__title {
        align-items: flex-start;
        font-size: 17px;
    }

    .report-annulled-warning__details {
        grid-template-columns: 1fr;
    }

    .report-card-page {
        padding: 0 8px 12px;
    }

    .report-card-header {
        align-items: center;
        background: rgba(244, 246, 248, .96);
        flex-wrap: wrap;
        gap: 8px;
        margin: 0 -8px 8px;
        padding: 8px;
        position: sticky;
        top: 0;
        z-index: 20;
    }

    .report-card-header .btn {
        flex: 0 0 auto;
    }

    .report-card-header__title {
        min-width: 0;
    }

    .report-card-header__actions {
        flex: 1 0 100%;
        margin-left: 0;
    }

    .report-card-header__actions .btn {
        width: 100%;
    }

    .report-card-header__title h5 {
        font-size: 16px;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .report-object-link,
    .report-card-header__title span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .report-summary-main,
    .report-explanations,
    .report-notes-grid {
        grid-template-columns: 1fr;
    }

    .report-card-content {
        gap: 8px;
    }

    .report-panel {
        border-radius: 6px;
        padding: 10px;
    }

    .report-summary-main {
        gap: 8px;
    }

    .report-summary-main > div {
        background: #f8fafc;
        border: 1px solid #edf1f5;
        border-radius: 6px;
        padding: 8px;
    }

    .report-summary-main small {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
    }

    .report-section-title {
        align-items: flex-start;
        flex-direction: column;
    }

    .report-section-actions,
    .report-current-progress-link,
    .report-media-action-buttons,
    .report-media-action-buttons .btn,
    .report-telegram-actions,
    .report-telegram-actions .btn {
        width: 100%;
    }

    .report-section-actions {
        align-items: stretch;
    }

    .report-current-progress-link,
    .report-media-action-buttons .btn,
    .report-telegram-actions .btn {
        justify-content: center;
    }

    .report-section-meta {
        flex-wrap: wrap;
    }

    .report-panel .table-responsive {
        overflow: visible;
    }

    .report-table {
        border-collapse: separate;
        border-spacing: 0;
    }

    .report-table thead {
        display: none;
    }

    .report-table,
    .report-table tbody,
    .report-table tr,
    .report-table td {
        display: block;
        width: 100%;
    }

    .report-table tr {
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        margin-bottom: 8px;
        padding: 9px;
    }

    .report-works-table {
        margin-top: 0;
    }

    .report-works-table tr {
        border: 1px solid #cbd5e0;
        border-left: 4px solid #6c8ebf;
        box-shadow: 0 2px 6px rgba(15, 23, 42, .1);
        margin-bottom: 12px;
        padding: 12px;
    }

    .report-works-table tbody tr td,
    .report-works-table tbody tr:nth-child(even) td {
        background: transparent;
    }

    .report-table td {
        border: 0 !important;
        padding: 0 0 8px !important;
        text-align: left !important;
    }

    .report-table td:last-child {
        padding-bottom: 0 !important;
    }

    .report-table td::before {
        color: #6c757d;
        content: attr(data-label);
        display: block;
        font-size: 11px;
        font-weight: 800;
        margin-bottom: 2px;
        text-transform: uppercase;
    }

    .report-workers-col {
        min-width: 0;
        width: auto;
    }

    .report-work-progress {
        min-width: 0;
    }

    .report-work-progress__meta {
        flex-direction: column;
        gap: 1px;
    }

    .report-material-item {
        padding-left: 7px;
    }

    .report-material-item__header {
        flex-direction: column;
        gap: 4px;
    }

    .report-material-scale {
        min-width: 0;
        width: 100%;
    }

    .report-material-scale__label {
        font-size: 8px;
    }

    .report-material-item__meta {
        gap: 4px 8px;
    }

    .report-team-access__chips {
        flex-wrap: nowrap;
        overflow-x: auto;
        padding-bottom: 2px;
    }

    .report-team-access__chip {
        flex: 0 0 auto;
    }

    .report-telegram-text {
        max-height: 55vh;
    }
}
</style>
